import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import StaffPicker from "../components/StaffPicker";
import SmartClientMatterSearch from "../components/SmartClientMatterSearch";
import { RecordTimeField, EMPTY_TIME, recordTouchpointTime, timeFieldProblem } from "../components/time/RecordTimeField";
import { searchMatters as searchSharePointMatters, MATTER_SEARCH_UNAVAILABLE_MESSAGE } from "../lib/matterSearch";
import { STAFF } from "../config/staffConfig";
import {
  FileText,
  User,
  CheckCircle2,
  XCircle,
  Pause,
  ChevronLeft,
  Sparkles,
  AlertCircle,
  Calendar,
  Loader2,
  Check,
  X,
  Search,
  ExternalLink,
  Upload,
  Mail,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fmtUK } from "../lib/dateUtils";
import { toast } from "sonner";
import BonsaiMate from "../components/BonsaiMate";

// BM-H4537: the folder post files into by default. Same order as the
// backend's pick_attendances_folder: a name with "attendances", then any
// "attendan…" name ("Attendance notes"); none means the matter root.
function pickAttendancesFolder(folders) {
  const names = (folders || []).map((f) => f?.name || "");
  return (
    names.find((n) => /attendances/i.test(n)) ||
    names.find((n) => /attendan/i.test(n)) ||
    ""
  );
}

function DocumentPreview({ postId, filename, sharepointId }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const loadPreview = async () => {
    if (!sharepointId || sharepointId.startsWith("manual-")) {
      setPreviewError("Document was created manually — no SharePoint file to preview");
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await axios.get(`${API}/postintake/queue/${postId}/preview`);
      setPreviewUrl(res.data.preview_url);
      setPreviewType(res.data.type);
    } catch (err) {
      setPreviewError(errorMessage(err, "Could not load preview"));
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => { loadPreview(); }, [postId, sharepointId]);

  if (previewUrl) {
    if (previewType === "web") {
      return (
        <div className="bg-slate-100 rounded-md border border-navy-border p-4" data-testid="pdf-preview">
          <p className="text-sm text-txt-muted mb-2">{filename}</p>
          <a href={previewUrl} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-teal text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-teal/90">
            <ExternalLink className="w-4 h-4" /> Open in SharePoint
          </a>
        </div>
      );
    }
    return (
      <div className="bg-slate-100 rounded-md overflow-hidden border border-navy-border" data-testid="pdf-preview">
        <iframe
          src={previewUrl}
          title={filename || "Document Preview"}
          className="w-full h-[600px]"
          style={{ border: "none", overflow: "auto" }}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
        <div className="p-2 border-t border-navy-border flex justify-end">
          <a href={previewUrl} target="_blank" rel="noopener noreferrer"
            className="text-xs text-teal hover:underline flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Open in new tab
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-100 rounded-md h-64 flex items-center justify-center border border-navy-border" data-testid="pdf-preview-placeholder">
      <div className="text-center text-slate-400 px-4">
        {previewLoading ? (
          <>
            <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
            <p className="text-sm">Loading preview...</p>
          </>
        ) : (
          <>
            <FileText className="w-10 h-10 mx-auto mb-2" />
            <p className="font-medium text-sm">{filename || "Document"}</p>
            {previewError && (
              <p className="text-xs mt-2 text-status-amber">{previewError}</p>
            )}
            {!previewError?.includes("manually") && (
              <button onClick={loadPreview} className="mt-2 text-xs text-teal hover:underline">Retry</button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function PostIntakeDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);

  // Auth user — loaded from localStorage (M365 session)
  const [user, setUser] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) setUser(JSON.parse(stored));
  }, []);

  const userEmail = user?.email || "";

  // Config from API
  const [documentTypes, setDocumentTypes] = useState([]);
  const [deadlineCategories, setDeadlineCategories] = useState([]);

  // Form state
  const [selectedMatter, setSelectedMatter] = useState("");
  const [selectedDocType, setSelectedDocType] = useState("");
  const [fromParty, setFromParty] = useState("");
  const [notes, setNotes] = useState("");
  const [responseRequired, setResponseRequired] = useState(false);
  const [responseDeadline, setResponseDeadline] = useState("");
  const [selectedDeadlines, setSelectedDeadlines] = useState([]);
  const [rejectReason, setRejectReason] = useState("");
  const [timeBox, setTimeBox] = useState(EMPTY_TIME); // BM-H4112

  // Auto-generated filing name
  const filingName = (() => {
    const parts = [];
    if (selectedMatter) parts.push(selectedMatter);
    const typeLabel = documentTypes.find(dt => dt.value === selectedDocType)?.label;
    if (typeLabel) parts.push(typeLabel);
    if (fromParty.trim()) parts.push(fromParty.trim());
    if (record?.received_at) {
      try { parts.push(fmtUK(record.received_at, "dd.MM.yyyy")); } catch {}
    }
    if (parts.length === 0) return "";
    const ext = record?.filename?.match(/\.[^.]+$/)?.[0] || ".pdf";
    return parts.join(" - ") + ext;
  })();

  // Matter picker — uses canonical SmartClientMatterSearch (mattersOnly).
  // Legacy dumb-substring /postintake/matters/search state removed 14 Jul
  // 2026: it returned zero hits for anything but exact prefix matches
  // (see screenshot "24 primrose" → no folders), which forced fee earners
  // to type the full canonical BL.XX.###.#### string. SmartClientMatterSearch
  // hits /smart-search (active-only status filter, AI top-match, client
  // collision annotation) which IS the canonical smart search primitive.

  // BM-H4029: SharePoint matter folders, searched beside CaseMinder. A matter
  // opened before CaseMinder, or closed, or post-completion, has a SharePoint
  // folder but no live CaseMinder record, so the CaseMinder search alone
  // cannot find it (8 Oct 2026: Emily could not find 77 Ashby Road, claimed
  // the post, and could not file it). The folder picked here goes to the
  // backend by id so the scan lands in exactly that folder.
  const [spQuery, setSpQuery] = useState("");
  const [spResults, setSpResults] = useState([]);
  const [spSearching, setSpSearching] = useState(false);
  const [spError, setSpError] = useState("");
  const [selectedFolder, setSelectedFolder] = useState(null);
  const spTimer = useRef(null);

  const handleMatterQuery = useCallback((q) => {
    setSpQuery(q);
    if (spTimer.current) clearTimeout(spTimer.current);
    const term = (q || "").trim();
    if (term.length < 3) { setSpResults([]); setSpError(""); return; }
    spTimer.current = setTimeout(async () => {
      setSpSearching(true);
      try {
        const results = await searchSharePointMatters(term);
        // Only matter folders carry a reference. A backend from before
        // BM-H4029 answers with the top-level drive folders (practice areas,
        // INCOMING POST), which must never be offered as a filing target.
        setSpResults(results.filter(r => r && r.reference));
        setSpError("");
      } catch (err) {
        setSpResults([]);
        setSpError(err?.message || MATTER_SEARCH_UNAVAILABLE_MESSAGE);
      } finally {
        setSpSearching(false);
      }
    }, 350);
  }, []);

  const pickSharePointFolder = (folder) => {
    setSelectedMatter(folder.reference || folder.name);
    setSelectedFolder(folder);
    setSpResults([]);
    setSpQuery("");
    setSubfolderQuery("");
    setSelectedSubfolder("");
  };

  // Subfolder picker — loaded when a matter is selected.
  const [subfolders, setSubfolders] = useState([]);
  const [loadingSubfolders, setLoadingSubfolders] = useState(false);
  const [subfolderError, setSubfolderError] = useState(null);
  const [selectedSubfolder, setSelectedSubfolder] = useState("");
  // BM-H4537: post files into the matter's Attendances folder unless someone
  // picks another folder or, deliberately, the matter root.
  const [toMatterRoot, setToMatterRoot] = useState(false);
  const [subfolderQuery, setSubfolderQuery] = useState("");
  const [showSubfolderDropdown, setShowSubfolderDropdown] = useState(false);

  // Fetch subfolders on matter select. Empty selectedMatter → clear.
  useEffect(() => {
    let cancelled = false;
    async function loadSubfolders() {
      if (!selectedMatter) {
        setSubfolders([]);
        setSelectedSubfolder("");
        setSubfolderError(null);
        return;
      }
      setLoadingSubfolders(true);
      setSubfolderError(null);
      try {
        const resp = await axios.get(
          `${API}/postintake/matters/${encodeURIComponent(selectedMatter)}/subfolders`,
          { params: selectedFolder?.id ? { folder_id: selectedFolder.id } : {} },
        );
        if (cancelled) return;
        const data = resp.data || {};
        const results = data.results || [];
        setSubfolders(results);
        setToMatterRoot(false);
        setSelectedSubfolder(pickAttendancesFolder(results));
        if (data.error) setSubfolderError(data.error);
      } catch (e) {
        if (cancelled) return;
        setSubfolders([]);
        setSubfolderError(errorMessage(e) || "Could not load subfolders");
      } finally {
        if (!cancelled) setLoadingSubfolders(false);
      }
    }
    loadSubfolders();
    return () => { cancelled = true; };
  }, [selectedMatter, selectedFolder]);

  // Smart client-side filter over the already-loaded subfolder list.
  // Token-tolerant: splits query on whitespace and requires each token to
  // appear anywhere in the name (case-insensitive). Matches the pattern
  // used across StaffPicker + SmartClientMatterSearch — no dumb single
  // substring gate.
  const filteredSubfolders = (() => {
    const q = (subfolderQuery || "").trim().toLowerCase();
    if (!q) return subfolders;
    const tokens = q.split(/\s+/).filter(Boolean);
    return subfolders.filter(f => {
      const name = (f.name || "").toLowerCase();
      return tokens.every(t => name.includes(t));
    });
  })();

  // Navigate to Client Doc Request pre-filled with current matter data
  const handleRequestDocuments = () => {
    const params = new URLSearchParams();
    if (selectedMatter) params.set("matter_ref", selectedMatter);
    // Pull client name + email from AI suggestions if available
    const suggestions = record?.ai_suggestions;
    if (suggestions?.client_name) params.set("client", suggestions.client_name);
    if (suggestions?.client_email) params.set("email", suggestions.client_email);
    // Pass back URL so the doc request page can offer "Back to this matter"
    params.set("return_to", `/post-intake/${postId}`);
    navigate(`/client-doc-request?${params.toString()}`);
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/postintake/config`);
      setDocumentTypes(response.data.document_types || []);
      setDeadlineCategories(response.data.deadline_categories || []);
    } catch {
      setDocumentTypes([
        { value: "court_order", label: "Court Order / Notice" },
        { value: "client_letter", label: "Client Letter" },
        { value: "third_party", label: "Third Party Correspondence" },
        { value: "invoice", label: "Invoice / Bill" },
        { value: "other", label: "Other" },
      ]);
      setDeadlineCategories([
        { value: "court_date", label: "Court Date", color: "text-status-red" },
        { value: "limitation_date", label: "Limitation Date", color: "text-purple-600" },
        { value: "client_deadline", label: "Client Deadline", color: "text-blue-600" },
        { value: "internal_reminder", label: "Internal Reminder", color: "text-txt-muted" },
      ]);
    }
  };

  const fetchRecord = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/postintake/queue/${postId}`);
      setRecord(response.data);

      if (response.data.ai_suggestions) {
        const suggestions = response.data.ai_suggestions;
        if (suggestions.matter?.matter_ref) {
          setSelectedMatter(suggestions.matter.matter_ref);
        }
        if (suggestions.document_type?.document_type) {
          setSelectedDocType(suggestions.document_type.document_type);
        }
        // BM-H4537: Claim & File is one press, so the sender the AI read is
        // filled in up front rather than only after a manual re-analyse.
        if (suggestions.from_party) {
          setFromParty((prev) => prev || suggestions.from_party);
        }
        if (suggestions.deadlines) {
          const highConfidence = suggestions.deadlines
            .filter(d => d.confidence >= 0.7)
            .map((_, idx) => idx);
          setSelectedDeadlines(highConfidence);
        }
      }
    } catch (error) {
      console.error("Failed to fetch record:", error);
      toast.error("Failed to load document");
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchRecord();
    fetchConfig();
  }, [fetchRecord]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/postintake/queue/${postId}/analyze`);
      if (response.data.suggestions) {
        setRecord(prev => ({
          ...prev,
          ai_suggestions: response.data.suggestions,
          ai_processed: true
        }));
        toast.success("AI analysis complete");

        const suggestions = response.data.suggestions;
        if (suggestions.matter?.matter_ref && !selectedMatter) {
          setSelectedMatter(suggestions.matter.matter_ref);
        }
        if (suggestions.document_type?.document_type && !selectedDocType) {
          setSelectedDocType(suggestions.document_type.document_type);
        }
        if (suggestions.from_party && !fromParty) {
          setFromParty(suggestions.from_party);
        }
      }
    } catch (error) {
      console.error("Analysis failed:", error);
      toast.error("AI analysis failed — document may not be available in SharePoint");
    } finally {
      setAnalyzing(false);
    }
  };


  // Claim-to-fee-earner state (front+back for the incoming-post triage flow).
  // Support staff often triage the queue on behalf of a fee earner: they need
  // to (a) claim the doc, (b) tell CaseMinder who owns it, (c) email that
  // person a copy of the PDF so they can act without hunting for the file.
  const [showClaimToFE, setShowClaimToFE] = useState(false);
  const [assignFeName, setAssignFeName] = useState("");
  const [assignNote, setAssignNote] = useState("");
  const [claimingToFE, setClaimingToFE] = useState(false);

  // Resolve the picked STAFF full_name back to the canonical email so we
  // don't rely on a name-→-email lookup on the backend. StaffPicker returns
  // the full_name; STAFF is the frontend source of truth for the roster.
  const assignFeEmail = (() => {
    const staff = STAFF.find(s => s.full_name === assignFeName);
    return staff?.email || "";
  })();

  const handleClaimToFeeEarner = async () => {
    if (!assignFeName || !assignFeEmail) {
      toast.error("Pick a fee earner");
      return;
    }
    // BM-H4537: this claims AND files, so check the filing details first.
    if (fileMissing.length) {
      toast.error(`To claim and file it, add: ${fileMissing.join(", ")}`);
      setShowClaimToFE(false);
      scrollToFile();
      return;
    }
    setClaimingToFE(true);
    try {
      const resp = await axios.post(
        `${API}/postintake/queue/${postId}/claim-to-fee-earner`,
        {
          claimed_by_email: userEmail,
          fee_earner_name: assignFeName,
          fee_earner_email: assignFeEmail,
          note: assignNote.trim() || null,
        }
      );
      const data = resp.data || {};
      setRecord(prev => ({
        ...prev,
        status: "claimed",
        claimed_by: userEmail,
        assigned_fee_earner_name: assignFeName,
        assigned_fee_earner_email: assignFeEmail,
        assigned_note: assignNote.trim() || null,
      }));
      // Surface the notify outcome so the user knows if email or Teams failed.
      const emailBits = data.notify_email_status === "sent"
        ? `emailed ${assignFeName}${data.pdf_attached ? " with PDF" : " (PDF not attached)"}`
        : `email ${data.notify_email_status}${data.notify_email_reason ? ` (${data.notify_email_reason})` : ""}`;
      const teamsBits = data.notify_teams_status === "sent"
        ? " • Teams sent"
        : (data.notify_teams_status === "skipped" ? "" : ` • Teams ${data.notify_teams_status}`);
      toast.success(`Claimed: ${emailBits}${teamsBits}`);
      setShowClaimToFE(false);
      setAssignFeName("");
      setAssignNote("");
      // BM-H4537: and file it, in the same press.
      await handleApprove();
    } catch (error) {
      toast.error(errorMessage(error, "Failed to claim and assign"));
    } finally {
      setClaimingToFE(false);
    }
  };

  const handlePark = async () => {
    try {
      await axios.post(`${API}/postintake/queue/${postId}/park`, {
        user_email: userEmail
      });
      setRecord(prev => ({ ...prev, status: "parked" }));
      toast.success("Document parked");
    } catch {
      toast.error("Failed to park document");
    }
  };

  const handleApprove = async () => {
    if (!selectedMatter) {
      toast.error("File Reference is required — please assign a matter");
      return;
    }
    if (!fromParty.trim()) {
      toast.error("From Party is required — please specify who the document is from");
      return;
    }
    if (!selectedDocType) {
      toast.error("Please select a document type");
      return;
    }
    if (timeFieldProblem(timeBox)) {
      toast.error(timeFieldProblem(timeBox));
      return;
    }

    setSubmitting(true);
    try {
      const deadlinesToCreate = selectedDeadlines.map(idx =>
        record.ai_suggestions?.deadlines?.[idx]
      ).filter(Boolean);

      const response = await axios.post(`${API}/postintake/queue/${postId}/approve`, {
        matter_id: selectedMatter,
        matter_ref: selectedMatter,
        document_type: selectedDocType,
        filing_name: filingName || null,
        from_party: fromParty.trim() || null,
        user_email: userEmail,
        notes: notes,
        deadlines_to_create: deadlinesToCreate,
        response_required: responseRequired,
        response_deadline: responseDeadline || null,
        subfolder: selectedSubfolder || null,
        to_matter_root: !selectedSubfolder && toMatterRoot,
        sharepoint_folder_id: selectedFolder?.id || null,
      });

      const calMsg = response.data.calendar_events_queued > 0 ? ` ${response.data.calendar_events_queued} calendar event(s) queued.` : "";
      const todoMsg = response.data.todo_task_queued ? " Response task created." : "";
      toast.success(`Filed successfully. ${response.data.deadlines_created} deadline(s) created.${calMsg}${todoMsg}`);
      // BM-H4112: time for dealing with this post. The post is filed either
      // way; a refusal (for example CCL not yet agreed) is said plainly.
      const t = await recordTouchpointTime({
        value: timeBox, matterRef: selectedMatter, source: "post", sourceRef: postId,
      });
      if (t.recorded) toast.success("Time recorded");
      else if (t.error) toast.error(`Post filed, but time not recorded: ${t.error}`);
      navigate("/post-intake");
    } catch (error) {
      console.error("Approval failed:", error);
      // BM-H4029: say why. "Nothing was filed" from the backend is the
      // sentence the user needs; a generic toast hid it.
      toast.error(errorMessage(error, "Failed to approve and file"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason) {
      toast.error("Please provide a rejection reason");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/postintake/queue/${postId}/reject`, {
        reason: rejectReason,
        user_email: userEmail
      });
      toast.success("Document rejected");
      navigate("/post-intake");
    } catch {
      toast.error("Failed to reject document");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailLink = async () => {
    setSendingLink(true);
    try {
      const userId =
        sessionStorage.getItem("bonsai_user_id") ||
        localStorage.getItem("bonsai_user_id") ||
        userEmail;
      const res = await axios.post(
        `${API}/postintake/incoming/${postId}/email-link`,
        {},
        { headers: { "X-Bonsai-User-Id": userId || "" } },
      );
      toast.success(`Link sent to ${res.data.to || "your email"}`);
    } catch (err) {
      toast.error(`Failed to send link — ${errorMessage(err, "Unknown error")}`);
    } finally {
      setSendingLink(false);
    }
  };

  const formatDate = (dateStr) => fmtUK(dateStr, "dd MMM yyyy");

  const getCategoryBadge = (category) => {
    const cat = deadlineCategories.find(c => c.value === category);
    const label = cat?.label || category;
    const color = cat?.color || "text-txt-muted";
    return <span className={`text-xs font-medium ${color}`}>{label}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-teal border-t-transparent rounded-md animate-spin" />
          <p className="mt-4 text-slate-500">Loading document...</p>
        </div>
      </div>
    );
  }

  if (!record) {
    return (
      <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
        <p className="text-slate-500">Document not found</p>
      </div>
    );
  }

  const suggestions = record.ai_suggestions;
  const isEditable = record.status === "pending" || record.status === "claimed";
  // BM-H4537: say what still stops Approve & File, instead of a silent grey button.
  const fileMissing = [
    !selectedMatter && "matter",
    !selectedDocType && "document type",
    !fromParty.trim() && "who it is from",
  ].filter(Boolean);
  const scrollToFile = () => {
    document.querySelector('[data-testid="approve-btn"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const handleClaimAndFile = () => {
    if (fileMissing.length) {
      toast.error(`To claim and file it, add: ${fileMissing.join(", ")}`);
      scrollToFile();
      return;
    }
    handleApprove();
  };

  return (
    <>
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/post-intake")}
                className="gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Queue
              </Button>
              <div>
                <h1 className="text-lg font-bold text-white" data-testid="post-detail-filename">{record.filename}</h1>
                <p className="text-sm text-slate-500">
                  Received: {formatDate(record.received_at)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Request Documents — always visible when a matter is assigned */}
              {selectedMatter && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRequestDocuments}
                  className="gap-1.5 border-cyan-600/40 text-cyan-300 hover:bg-cyan-900/20"
                  data-testid="request-docs-btn"
                >
                  <Upload className="w-4 h-4" />
                  Request Documents
                </Button>
              )}
              {record.status === "claimed" && (
                <Badge
                  variant="outline"
                  className="bg-status-amber-dim text-status-amber border-amber-200 gap-1"
                  title={`Claimed by ${record.claimed_by || "someone"} before claiming filed the letter.`}
                  data-testid="claimed-not-filed"
                >
                  <AlertCircle className="w-3 h-3" />
                  Claimed, not on the matter yet
                </Badge>
              )}
              {isEditable && (
                <>
                  {/* BM-H4537: claiming IS filing (Vanessa, 20 Oct 2026).
                      There used to be a "Claim (just me)" that only recorded
                      an owner, and letters sat claimed in INCOMING POST. */}
                  <Button
                    onClick={handleClaimAndFile}
                    disabled={submitting}
                    className="bg-teal hover:bg-teal/90"
                    data-testid="claim-btn"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Claim & File
                  </Button>
                  {record.status === "pending" && (
                    <Button
                      variant="outline"
                      onClick={() => setShowClaimToFE(true)}
                      data-testid="claim-to-fee-earner-btn"
                    >
                      <User className="w-4 h-4 mr-1" />
                      Claim, File & Tell
                    </Button>
                  )}
                  <Button variant="outline" onClick={handlePark} data-testid="park-btn">
                    <Pause className="w-4 h-4 mr-1" />
                    Park
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Document Preview */}
          <div className="bg-white rounded-md border border-navy-border p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-white">Document Preview</h2>
              {!record.ai_processed && isEditable && (
                <Button
                  onClick={handleAnalyze}
                  disabled={analyzing}
                  className="gap-2 bg-purple-600 hover:bg-purple-700"
                  data-testid="analyze-btn"
                >
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {analyzing ? "Analyzing..." : "Run AI Analysis"}
                </Button>
              )}
              {record.ai_processed && (
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 gap-1">
                  <Sparkles className="w-3 h-3" />
                  AI Analyzed
                </Badge>
              )}
            </div>
            <DocumentPreview postId={postId} filename={record.filename} sharepointId={record.sharepoint_id} />
          </div>

          {/* Right: AI Suggestions & Form */}
          <div className="space-y-6">
            {/* AI Summary */}
            {suggestions && (
              <div className="bg-purple-50 rounded-md border border-purple-200 p-4" data-testid="ai-suggestions">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-purple-800">AI Analysis</h3>
                </div>
                {suggestions.summary && (
                  <div className="bg-white/60 rounded-md p-3 mb-3 border border-purple-100">
                    <p className="text-sm text-purple-800 leading-relaxed" data-testid="ai-summary">{suggestions.summary}</p>
                  </div>
                )}
                {suggestions.error && (
                  <p className="text-sm text-status-red mb-3">Analysis error: {suggestions.error}</p>
                )}
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <span className="text-purple-600 font-medium min-w-[80px]">Matter:</span>
                    <span className="font-medium text-purple-900">
                      {suggestions.matter?.matter_ref || "Not identified"}
                      {suggestions.matter?.confidence > 0 && (
                        <span className="text-purple-400 ml-1 text-xs">({Math.round(suggestions.matter.confidence * 100)}% confidence)</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-purple-600 font-medium min-w-[80px]">Type:</span>
                    <span className="font-medium text-purple-900 capitalize">
                      {suggestions.document_type?.document_type?.replace(/_/g, " ") || "Not identified"}
                    </span>
                  </div>
                  {suggestions.from_party && (
                    <div className="flex items-start gap-2">
                      <span className="text-purple-600 font-medium min-w-[80px]">From:</span>
                      <span className="font-medium text-purple-900">{suggestions.from_party}</span>
                    </div>
                  )}
                  {suggestions.deadlines && suggestions.deadlines.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="text-purple-600 font-medium min-w-[80px]">Deadlines:</span>
                      <span className="font-medium text-purple-900">{suggestions.deadlines.length} detected</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Form */}
            {isEditable && (
              <div className="bg-white rounded-md border border-navy-border p-4 space-y-4" data-testid="filing-form">
                <h3 className="font-semibold text-white">Filing Details</h3>

                <div>
                  <label className="block text-sm font-medium text-txt mb-1">
                    File Reference <span className="text-status-red">*</span>
                    {!selectedMatter && (
                      <span className="ml-2 text-xs font-bold text-status-red bg-status-red-dim px-2 py-0.5 rounded" data-testid="matter-required">MUST BE COMPLETED</span>
                    )}
                  </label>
                  {selectedMatter ? (
                    <div className="flex items-center gap-2 p-2 bg-teal-dim rounded-md border border-teal">
                      <span className="text-sm font-medium text-teal flex-1" data-testid="selected-matter">
                        {selectedMatter}
                        {selectedFolder && (
                          <span className="block text-xs font-normal text-slate-500" data-testid="selected-sharepoint-folder">
                            SharePoint: {selectedFolder.path}
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() => { setSelectedMatter(""); setSelectedFolder(null); setSelectedSubfolder(""); setSubfolderQuery(""); }}
                        className="text-slate-400 hover:text-status-red"
                        data-testid="clear-matter-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <SmartClientMatterSearch
                      label=""
                      placeholder="Search by client, address, or matter reference..."
                      mattersOnly={true}
                      darkMode={false}
                      showMatterRef={true}
                      onQueryChange={handleMatterQuery}
                      onSelect={(result) => {
                        // BM-H4029: ignore what the component propagates as
                        // the user types. Its "looks like a reference" test
                        // passes any word of four letters or more, so typing
                        // "Ashby" used to select "Ashby" as the File
                        // Reference and close the search before any result
                        // could be picked. A matter is chosen by clicking a
                        // CaseMinder or SharePoint result.
                        if (result?.type === "typed") return;
                        const ref = result?.matter_reference || result?.matter_ref || "";
                        if (ref) {
                          setSelectedMatter(ref);
                          setSelectedFolder(null);
                          setSubfolderQuery("");
                          setSelectedSubfolder("");
                        }
                      }}
                    />
                  )}
                  {!selectedMatter && spQuery.trim().length >= 3 && (
                    <div className="mt-2 rounded-md border border-slate-200" data-testid="sharepoint-matter-results">
                      <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 border-b border-slate-100">
                        SharePoint matter folders
                        {spSearching && <Loader2 className="inline w-3 h-3 ml-2 animate-spin" />}
                      </div>
                      {spError ? (
                        <p className="px-3 py-2 text-xs text-status-red" data-testid="sharepoint-matter-error">{spError}</p>
                      ) : !spSearching && spResults.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-500">No SharePoint matter folder matches “{spQuery.trim()}”.</p>
                      ) : (
                        <div className="max-h-56 overflow-y-auto">
                          {spResults.map(folder => (
                            <button
                              key={folder.id}
                              type="button"
                              onClick={() => pickSharePointFolder(folder)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--surface-inset)] border-b border-slate-100 last:border-0"
                              data-testid="sharepoint-matter-option"
                            >
                              <span className="font-medium">{folder.name}</span>
                              {folder.closed && (
                                <span className="ml-2 text-[10px] font-semibold uppercase text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Closed</span>
                              )}
                              <span className="block text-xs text-slate-400">{folder.path}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Subfolder picker — shown once a matter is selected.
                    Standard skeleton = Attendances, Compliance and Financials,
                    Documents, Drafts; residential matters get extra subfolders
                    (Client Protocol Forms, Deeds, Original documents received,
                    Searches and Indemnity …). We show what actually exists in
                    SharePoint plus the option to file to matter root. */}
                {selectedMatter && (
                  <div>
                    <label className="block text-sm font-medium text-txt mb-1">
                      Subfolder
                      <span className="ml-2 text-xs font-normal text-slate-400">
                        (Attendances unless you pick another)
                      </span>
                    </label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={selectedSubfolder || subfolderQuery}
                        onChange={(e) => {
                          setSubfolderQuery(e.target.value);
                          setSelectedSubfolder("");
                          setShowSubfolderDropdown(true);
                        }}
                        onFocus={() => setShowSubfolderDropdown(true)}
                        onBlur={() => setTimeout(() => setShowSubfolderDropdown(false), 200)}
                        placeholder={
                          loadingSubfolders
                            ? "Loading subfolders…"
                            : subfolders.length === 0
                              ? (subfolderError ? "No subfolders (matter root only)" : "No subfolders found")
                              : "Type to filter, e.g. attend, corr, deeds…"
                        }
                        className="pl-10"
                        disabled={loadingSubfolders}
                        data-testid="subfolder-search-input"
                      />
                      {loadingSubfolders && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                      )}
                      {showSubfolderDropdown && !loadingSubfolders && subfolders.length > 0 && (
                        <div
                          className="absolute z-20 w-full mt-1 bg-white border border-navy-border rounded-md shadow-lg max-h-56 overflow-y-auto"
                          data-testid="subfolder-results"
                        >
                          <button
                            className="w-full text-left px-3 py-2 text-sm hover:bg-navy-hover/60 border-b border-navy-border italic text-slate-500"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setSelectedSubfolder("");
                              setToMatterRoot(true);
                              setSubfolderQuery("");
                              setShowSubfolderDropdown(false);
                            }}
                            data-testid="subfolder-root-option"
                          >
                            Matter root (no subfolder)
                          </button>
                          {filteredSubfolders.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-slate-500">
                              No subfolders match “{subfolderQuery}”
                            </div>
                          ) : (
                            filteredSubfolders.map((f) => (
                              <button
                                key={f.name}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-navy-hover/60 border-b border-navy-border last:border-0"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setSelectedSubfolder(f.name);
                                  setToMatterRoot(false);
                                  setSubfolderQuery("");
                                  setShowSubfolderDropdown(false);
                                }}
                                data-testid={`subfolder-option-${f.name}`}
                              >
                                <span className="font-medium text-white">{f.name}</span>
                                {f.childCount > 0 && (
                                  <span className="text-xs text-slate-400 ml-2">{f.childCount} items</span>
                                )}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    {selectedSubfolder && (
                      <p className="mt-1 text-xs text-teal">
                        Filing to: <span className="font-medium">{selectedMatter} / {selectedSubfolder}</span>
                      </p>
                    )}
                    {!selectedSubfolder && !loadingSubfolders && (
                      <p className="mt-1 text-xs text-slate-500">
                        Filing to: <span className="font-medium">{selectedMatter}</span> (root)
                      </p>
                    )}
                    {subfolderError && subfolders.length === 0 && (
                      <p className="mt-1 text-xs text-status-red">{subfolderError}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-txt mb-1">
                    Document Type *
                  </label>
                  <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                    <SelectTrigger data-testid="doctype-select">
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {documentTypes.map(dt => (
                        <SelectItem key={dt.value} value={dt.value}>
                          {dt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-txt mb-1">
                    From Party <span className="text-status-red">*</span>
                    {!fromParty.trim() && (
                      <span className="ml-2 text-xs font-bold text-status-red bg-status-red-dim px-2 py-0.5 rounded" data-testid="from-party-required">MUST BE COMPLETED</span>
                    )}
                  </label>
                  <Input
                    value={fromParty}
                    onChange={(e) => setFromParty(e.target.value)}
                    placeholder="e.g. Smith & Jones Solicitors"
                    className={`${!fromParty.trim() ? "border-red-300 bg-status-red-dim/30" : "border-teal bg-teal-dim/30"}`}
                    data-testid="from-party-input"
                  />
                </div>

                {filingName && (
                  <div className="bg-navy-hover/60 rounded-md p-3 border border-navy-border" data-testid="filing-name-preview">
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Filing Name
                    </label>
                    <p className="text-sm font-medium text-white break-all">{filingName}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-txt mb-1">
                    Notes
                  </label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional notes..."
                    rows={2}
                    data-testid="notes-input"
                  />
                </div>
              </div>
            )}

            {/* Deadlines */}
            {suggestions?.deadlines?.length > 0 && (
              <div className="bg-white rounded-md border border-navy-border p-4" data-testid="deadlines-section">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-status-amber" />
                  <h3 className="font-semibold text-white">
                    Extracted Deadlines ({suggestions.deadlines.length})
                  </h3>
                </div>
                <p className="text-sm text-slate-500 mb-3">
                  Select deadlines to create as tasks:
                </p>
                <div className="space-y-2">
                  {suggestions.deadlines.map((dl, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-md border transition-colors ${
                        selectedDeadlines.includes(idx)
                          ? "bg-status-amber-dim border-amber-300"
                          : "bg-navy-hover/60 border-navy-border"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {isEditable && (
                          <Checkbox
                            checked={selectedDeadlines.includes(idx)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedDeadlines([...selectedDeadlines, idx]);
                              } else {
                                setSelectedDeadlines(selectedDeadlines.filter(i => i !== idx));
                              }
                            }}
                            data-testid={`deadline-checkbox-${idx}`}
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-white">{dl.description}</p>
                            <span className="text-xs text-slate-400">
                              {Math.round(dl.confidence * 100)}% confidence
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm">
                            {dl.due_date && (
                              <span className="flex items-center gap-1 text-txt-muted">
                                <Calendar className="w-3 h-3" />
                                {formatDate(dl.due_date)}
                              </span>
                            )}
                            {getCategoryBadge(dl.category)}
                          </div>
                          {dl.source_text && (
                            <p className="text-xs text-slate-400 mt-1 italic">
                              &ldquo;{dl.source_text}&rdquo;
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Response Required Toggle */}
            {isEditable && (
              <div className="bg-navy-hover/60 rounded-md border border-navy-border p-4" data-testid="response-required-section">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="font-semibold text-sm text-txt">Response Letter Required</label>
                    <p className="text-xs text-slate-500 mt-0.5">Creates a To-Do task with deadline</p>
                  </div>
                  <button
                    onClick={() => setResponseRequired(!responseRequired)}
                    className={`relative w-11 h-6 rounded-md transition-colors ${
                      responseRequired ? "bg-teal" : "bg-slate-300"
                    }`}
                    data-testid="response-required-toggle"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-md bg-white transition-transform shadow-sm ${
                      responseRequired ? "translate-x-5" : ""
                    }`} />
                  </button>
                </div>
                {responseRequired && (
                  <div className="mt-3">
                    <label className="text-xs font-medium text-txt-muted">Response Deadline</label>
                    <Input
                      type="date"
                      value={responseDeadline}
                      onChange={e => setResponseDeadline(e.target.value)}
                      className="mt-1 h-8 text-sm"
                      data-testid="response-deadline-input"
                    />
                  </div>
                )}
              </div>
            )}

            {/* BM-H4112: optional time for dealing with this post */}
            {isEditable && (
              <RecordTimeField
                value={timeBox}
                onChange={setTimeBox}
                defaultNarrative={fromParty.trim() ? `Perusing letter from ${fromParty.trim()}` : "Perusing letter"}
                testIdPrefix="post-time"
                light
              />
            )}

            {/* Actions */}
            {isEditable && (
              <div className="flex flex-col gap-3" data-testid="action-buttons">
                <Button
                  onClick={handleApprove}
                  disabled={submitting || !selectedMatter || !selectedDocType || !fromParty.trim()}
                  className="w-full bg-teal hover:bg-teal/90 gap-2"
                  data-testid="approve-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Claim & File
                </Button>
                {fileMissing.length > 0 && (
                  <p className="text-xs text-status-amber" data-testid="approve-missing">
                    To file, add: {fileMissing.join(", ")}
                  </p>
                )}

                <div className="flex gap-2">
                  <Input
                    placeholder="Rejection reason..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="flex-1"
                    data-testid="reject-reason-input"
                  />
                  <Button
                    variant="outline"
                    onClick={handleReject}
                    disabled={submitting || !rejectReason}
                    className="text-status-red border-red-200 hover:bg-status-red-dim"
                    data-testid="reject-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Reject
                  </Button>
                </div>
              </div>
            )}

            {/* Completed Status */}
            {record.status === "approved" && (
              <div className="bg-teal-dim rounded-md border border-teal p-4" data-testid="approved-status">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-teal" />
                  <span className="font-medium text-teal">Filed Successfully</span>
                </div>
                <p className="text-sm text-teal mt-1">
                  Filed to: {record.filed_to_path || "Unknown"}
                </p>
                <p className="text-sm text-teal">
                  By: {record.filed_by} on {formatDate(record.filed_at)}
                </p>
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEmailLink}
                    disabled={sendingLink}
                    className="gap-2 border-teal text-teal hover:bg-teal/10"
                    data-testid="email-saved-link-btn"
                  >
                    {sendingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    Send me a link to the saved document
                  </Button>
                </div>
                {(record.calendar_synced_at || record.todo_synced_at || record.response_required) && (
                  <div className="mt-3 pt-3 border-t border-teal space-y-1.5">
                    {record.calendar_synced_at && (
                      <div className="flex items-center gap-2 text-sm text-teal">
                        <Calendar className="w-4 h-4" />
                        <span>Calendar events synced</span>
                      </div>
                    )}
                    {record.todo_synced_at && (
                      <div className="flex items-center gap-2 text-sm text-teal" data-testid="todo-synced">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Response task created{record.response_deadline ? ` (due ${formatDate(record.response_deadline)})` : ""}</span>
                      </div>
                    )}
                    {record.response_required && !record.todo_synced_at && (
                      <div className="flex items-center gap-2 text-sm text-status-amber" data-testid="todo-pending">
                        <AlertCircle className="w-4 h-4" />
                        <span>Response task pending sync</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {record.status === "rejected" && (
              <div className="bg-status-red-dim rounded-md border border-red-200 p-4" data-testid="rejected-status">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-status-red" />
                  <span className="font-medium text-status-red">Rejected</span>
                </div>
                <p className="text-sm text-status-red mt-1">
                  Reason: {record.rejection_reason}
                </p>
                <p className="text-sm text-status-red">
                  By: {record.rejected_by} on {formatDate(record.rejected_at)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    {selectedMatter && (
      <BonsaiMate context="matter" matterReference={selectedMatter} />
    )}

    {/* Claim & Assign to Fee Earner modal.
        Support staff (post-scanning, admin, clientcare) triage the incoming
        queue on behalf of the fee earner who owns each letter/court doc. This
        modal captures who owns it + an optional handoff note, then the backend
        claims the record, emails the fee earner with a PDF copy, and pings
        Teams (if a webhook is configured). Best-effort — the claim never
        rolls back if a notification fails. */}
    <Dialog open={showClaimToFE} onOpenChange={setShowClaimToFE}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Claim &amp; Assign to Fee Earner</DialogTitle>
          <DialogDescription>
            Pick who owns this incoming letter. They’ll be emailed a copy of the PDF and pinged in Teams so they can act without hunting for the file.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="block text-sm font-medium text-txt mb-1">
              Fee earner <span className="text-status-red">*</span>
            </label>
            <StaffPicker
              value={assignFeName}
              onChange={setAssignFeName}
              placeholder="Search or pick a fee earner"
              testId="assign-fe-picker"
            />
            {assignFeName && assignFeEmail && (
              <p className="text-xs text-slate-500 mt-1">Will notify: {assignFeEmail}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-txt mb-1">
              Handoff note <span className="text-slate-400 text-xs">(optional)</span>
            </label>
            <Textarea
              value={assignNote}
              onChange={e => setAssignNote(e.target.value)}
              placeholder="e.g. urgent — court deadline mid-August"
              rows={3}
              data-testid="assign-fe-note"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowClaimToFE(false)} disabled={claimingToFE}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="bg-teal hover:bg-teal/90 gap-1.5"
              onClick={handleClaimToFeeEarner}
              disabled={claimingToFE || !assignFeName || !assignFeEmail}
              data-testid="confirm-claim-to-fe-btn"
            >
              {claimingToFE ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {claimingToFE ? "Claiming…" : "Claim, File & Notify"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
